import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchFactionAssets,
  fetchFactionProfiles,
  fetchForcePresets,
  fetchLoadouts,
  type FactionAssets,
  type FactionProfileView,
  type ForcePresetBody,
  type ForcePresetView,
  type LoadoutView,
} from '../api/catalog';
import { ca } from '../i18n/ca';
import { AssetPicture } from './AssetPicture';
import { FactionEmblem } from './FactionEmblem';
import { FactionLooks } from './FactionLooks';
import { FactionPicker } from './FactionPicker';
import { LoadProblemNote } from './LoadProblemNote';
import { problemOf, type LoadProblem } from './loadProblem';
import { factionLabel } from './factionNames';
import { Details, QuickStart } from './NewMission';
import {
  STEP,
  STEP2_KEY,
  readDraft,
  readStored,
  summariesOf,
  writeDraft,
  type MissionDraftStep2,
  type PartialDraft,
  type PlayerRoleDraft,
} from './newMissionDraft';
import { Shell } from './Shell';
import { squadCount } from './squadPlan';
import { StartFromStepOne } from './StartFromStepOne';
import { useSettingDefaults } from './useSettingDefaults';

/**
 * The quick start, step 6: our own forces.
 *
 * The counterpart of the enemy step, and deliberately more detailed than it.
 * The enemy is a faction, a strength and a skill because nobody ever sees it
 * listed; our side is what the lobby shows and what everybody plays, so it is
 * edited the way Eden edits it — a faction out of the mod set's own catalogue,
 * a squad size, and a table of slots each with a role, a name, an exact man
 * class and a loadout.
 *
 * Until this existed the quick start sent `playerSide: "west"` and
 * `playerFaction: null` on every generation, so a group that plays as AAF, as
 * the Resistance or as a mod's own unit had to repair each mission in the
 * designer after it was made.
 *
 * It comes after the type step, not before it, because the recipe is what
 * proposes a squad: the table starts as the type's own and is edited from there.
 * Everything on this screen is optional — leave it alone and the generator does
 * exactly what it did.
 *
 * The faction is picked with the enemy step's own picker, and the details panel
 * beside it shows what the chosen one looks like: it was the only step with no
 * details and no soldiers in it (QUICK_START_REVIEW.md F8, F17). The side comes
 * with the faction; the enemy's side is not offered, because two factions of
 * one side are allies in the game and a night between them is a night nobody
 * fights. The base and the vehicles it also asked for moved to the next step,
 * "Sortida", with the ways in and out they depend on (F2).
 */

const SIDES = ['west', 'east', 'resistance'] as const;

/** The roles a slot can have, in the order a section is usually written. */
const ROLES = [
  'teamleader',
  'medic',
  'rto',
  'autorifleman',
  'at',
  'marksman',
  'engineer',
  'crew',
  'pilot',
  'rifleman',
];

const roleName = (role: string) => ca.missionUi.ours.slotRoleNames[role] ?? role;

export function NewMissionForces() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<PartialDraft>(() => readDraft());
  const step2 = readStored<MissionDraftStep2>(STEP2_KEY);
  /** The squad the type proposes, one slot per role, which the table starts from. */
  const proposal = (): PlayerRoleDraft[] =>
    (step2?.recipeRoles ?? []).map((role) => ({ role, count: 1 }));

  const [side, setSide] = useState<string>(draft.playerSide ?? 'west');
  const [faction, setFaction] = useState<string | null>(draft.playerFactionClass ?? null);
  /** The profile picked: a hand-made faction shares its class with the one it was made from. */
  const [profileId, setProfileId] = useState<number | null>(draft.playerProfileId ?? null);
  const [squadSize, setSquadSize] = useState<number>(draft.squadSize ?? 8);
  const [roles, setRoles] = useState<PlayerRoleDraft[]>(() =>
    draft.roles?.length ? draft.roles : proposal(),
  );
  /** Who flies the helicopter: a faction's own crew, from its squad; null for its pilot class. */
  const [aircrew, setAircrew] = useState<PlayerRoleDraft | null>(draft.aircrew ?? null);

  const [profiles, setProfiles] = useState<FactionProfileView[]>([]);
  /** Why the factions did not arrive, when they did not: the side select below is for a mod set with none. */
  const [profilesProblem, setProfilesProblem] = useState<LoadProblem | null>(null);
  const [profilesAsked, setProfilesAsked] = useState(0);
  const [assets, setAssets] = useState<FactionAssets | null>(null);
  /** The faction the assets above answered for, so the looks can tell "none" from "not yet". */
  const [assetsFor, setAssetsFor] = useState<string | null>(null);
  const [loadouts, setLoadouts] = useState<LoadoutView[]>([]);
  const [groupPresets, setGroupPresets] = useState<ForcePresetView[]>([]);

  useEffect(() => {
    if (!draft.modSetHash) return;
    setProfilesProblem(null);
    fetchFactionProfiles(draft.modSetHash)
      .then(setProfiles)
      .catch((err) => setProfilesProblem(problemOf(err)));
    fetchLoadouts(draft.modSetHash)
      .then(setLoadouts)
      .catch(() => setLoadouts([]));
    fetchForcePresets(draft.modSetHash, 'group')
      .then(setGroupPresets)
      .catch(() => setGroupPresets([]));
  }, [draft.modSetHash, profilesAsked]);

  useEffect(() => {
    if (!draft.modSetHash || !faction) {
      setAssets(null);
      setAssetsFor(null);
      return;
    }
    let live = true;
    fetchFactionAssets(draft.modSetHash, faction)
      .then((a) => {
        if (!live) return;
        setAssets(a);
        setAssetsFor(faction);
      })
      .catch(() => {
        if (!live) return;
        setAssets(null);
        setAssetsFor(faction);
      });
    return () => {
      live = false;
    };
  }, [draft.modSetHash, faction]);

  // Who we make sense as tonight: the era chosen at step 1 and where this terrain is.
  const { defaults } = useSettingDefaults(draft.serverId, draft.worldName, draft.era);

  // The enemy's side, from the step that chose it.
  const enemySide = profiles.find((p) => p.id === draft.enemyProfileId)?.side ?? null;

  // With nobody chosen, the first faction that fits the night is: every later
  // step — the ways in, the vehicles, the supports — asks what *our faction* has,
  // and "nobody" would be answered as NATO in 1942. Once, so a pick is never undone.
  const proposedOnce = useRef(false);
  useEffect(() => {
    if (proposedOnce.current || faction !== null || !defaults || profiles.length === 0) return;
    for (const ours of defaults.ours) {
      const fit = profiles.find(
        (p) =>
          p.factionClass.toLowerCase() === ours.className.toLowerCase() &&
          (SIDES as readonly string[]).includes(p.side) &&
          p.side !== enemySide,
      );
      if (fit) {
        proposedOnce.current = true;
        setFaction(fit.factionClass);
        setSide(fit.side);
        return;
      }
    }
  }, [defaults, profiles, faction, enemySide]);
  // The profile picked when there is one; by class otherwise, and then the
  // plain faction before one made from it: the 1RA plays as USMC, and a night
  // proposed as USMC is not a night proposed as the 1RA.
  const byClass = (p: FactionProfileView) => p.factionClass === faction;
  const chosen =
    profiles.find((p) => p.id === profileId && byClass(p)) ??
    profiles.find((p) => byClass(p) && p.side === side && !p.squadPresetId) ??
    profiles.find((p) => byClass(p) && p.side === side) ??
    profiles.find(byClass) ??
    null;

  // How many slots the table actually asks for, against how many people are
  // coming: a squad of eight written as ten slots is a table nobody can fill.
  const perSquad = roles.reduce((n, r) => n + (r.count ?? 1), 0);
  const squads = squadCount(step2?.players ?? 12, squadSize, roles);

  if (!draft.serverId || !draft.worldName || !step2?.recipeId) {
    return <StartFromStepOne />;
  }

  function setRole(index: number, patch: Partial<PlayerRoleDraft>) {
    setRoles((current) => current.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  /**
   * The squad, taken whole from a group preset. One click instead of ten rows,
   * which is the point of saving one: a group that always plays the same section
   * writes it once in the catalogue and picks it here every night.
   */
  function fromPreset(id: string): PlayerRoleDraft[] | null {
    const preset = groupPresets.find((p) => String(p.id) === id);
    if (!preset) return null;
    let body: ForcePresetBody;
    try {
      body = JSON.parse(preset.bodyJson) as ForcePresetBody;
    } catch {
      return null;
    }
    const squad = (body.members ?? []).map((m) => ({
      role: m.role || 'rifleman',
      label: m.label ?? undefined,
      unitClass: m.unitClass ?? null,
      loadoutId: m.loadoutId ?? null,
      count: m.count ?? 1,
    }));
    setRoles(squad);
    setAircrew(
      body.aircrew?.unitClass || body.aircrew?.loadoutId
        ? {
            role: 'pilot',
            unitClass: body.aircrew.unitClass ?? null,
            loadoutId: body.aircrew.loadoutId ?? null,
          }
        : null,
    );
    if (preset.factionClass) {
      setFaction(preset.factionClass);
      setSide(preset.side);
    }
    return squad;
  }

  /**
   * A faction picked. One made by hand can carry the squad it plays as — the
   * 1RA's, with Atunero's own kit — and then the table is that squad, and a
   * squad is as big as its slots.
   */
  function pickFaction(p: FactionProfileView) {
    setProfileId(p.id);
    setFaction(p.factionClass);
    setSide(p.side);
    // Another faction's crew does not fly this one's helicopter.
    setAircrew(null);
    if (!p.squadPresetId) return;
    const squad = fromPreset(String(p.squadPresetId));
    if (squad?.length) {
      setSquadSize(
        Math.max(
          2,
          Math.min(
            12,
            squad.reduce((n, r) => n + (r.count ?? 1), 0),
          ),
        ),
      );
    }
  }

  function move(index: number, by: number) {
    setRoles((current) => {
      const next = [...current];
      const to = index + by;
      if (to < 0 || to >= next.length) return current;
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  }

  function next() {
    // With no faction chosen the side is the select's, and never the enemy's.
    const playerSide =
      chosen?.side ?? (side === enemySide ? (SIDES.find((s) => s !== enemySide) ?? side) : side);
    setDraft(
      writeDraft({
        playerSide,
        playerFactionClass: chosen?.factionClass ?? null,
        playerFactionName: chosen ? factionLabel(chosen) : null,
        playerProfileId: chosen?.id ?? null,
        squadSize,
        // An untouched table is the recipe's own, and the generator already has
        // it: sending it back would freeze this mission's squad against a recipe
        // that may change.
        roles: roles.length ? roles : null,
        aircrew,
      }),
    );
    navigate('/new/start');
  }

  const squadLine = `${ca.missionUi.ours.squads(squads)} · ${ca.missionUi.ours.seats(perSquad)}`;

  const details = (
    <Details>
      {chosen ? (
        <>
          <p className="row who">
            <FactionEmblem
              name={factionLabel(chosen)}
              factionClass={chosen.factionClass}
              side={chosen.side}
              picture={chosen.picture}
              size={56}
            />
            <strong>{factionLabel(chosen)}</strong>
          </p>
          <dl className="facts">
            <dt>{ca.missionUi.ours.side}</dt>
            <dd>{ca.sideNames[chosen.side] ?? chosen.side}</dd>
            <dt>{ca.missionUi.details.enemyClass}</dt>
            <dd>{chosen.factionClass}</dd>
            <dt>{ca.missionUi.ours.squadTitle}</dt>
            <dd>{squadLine}</dd>
          </dl>
          <FactionLooks
            title={ca.missionUi.looks.ours}
            hint={ca.missionUi.looks.oursHint}
            units={assetsFor === faction ? (assets?.units ?? []) : null}
            modSetHash={draft.modSetHash}
          />
        </>
      ) : (
        <>
          <p className="muted small">{ca.missionUi.details.pickFirst}</p>
          <dl className="facts">
            <dt>{ca.missionUi.ours.squadTitle}</dt>
            <dd>{squadLine}</dd>
          </dl>
        </>
      )}
    </Details>
  );

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <>
          <Link className="button" to="/new/intel">
            {ca.back}
          </Link>
          <button type="button" className="primary" onClick={next}>
            {ca.next}
          </button>
        </>
      }
    >
      <QuickStart step={STEP.ours} summaries={summariesOf(draft, step2)} aside={details}>
        <section className="card form" aria-label={ca.missionUi.ours.pickFaction}>
          <h2>{ca.missionUi.ours.pickFaction}</h2>
          {profilesProblem && (
            <LoadProblemNote
              problem={profilesProblem}
              what="factions"
              onRetry={() => setProfilesAsked((n) => n + 1)}
            />
          )}
          {profiles.length > 0 ? (
            <>
              <FactionPicker
                profiles={profiles}
                value={chosen?.id ?? null}
                onChange={pickFaction}
                sides={SIDES}
                exclude={enemySide ? [enemySide] : []}
                first={(defaults?.ours ?? []).map((f) => f.className)}
                squadFirst
                recent={{ serverId: draft.serverId, step: 'forces' }}
                label={ca.missionUi.ours.pickFaction}
              />
              <p className="muted small">{ca.missionUi.ours.pickFactionHint}</p>
            </>
          ) : (
            <>
              <label>
                {ca.missionUi.ours.side}
                <select
                  name="playerSide"
                  value={side}
                  onChange={(e) => {
                    setSide(e.target.value);
                    setFaction(null);
                  }}
                >
                  {SIDES.map((s) => (
                    <option key={s} value={s}>
                      {ca.sideNames[s] ?? s}
                    </option>
                  ))}
                </select>
              </label>
              <p className="muted small">{ca.missionUi.ours.noAssets}</p>
            </>
          )}
        </section>

        <section className="card form" aria-label={ca.missionUi.ours.squadTitle}>
          <div className="row between">
            <h2>{ca.missionUi.ours.squadTitle}</h2>
            <span className="row">
              <button
                type="button"
                className="small"
                onClick={() => setRoles((c) => [...c, { role: 'rifleman', count: 1 }])}
              >
                {ca.missionUi.ours.roleAdd}
              </button>
              <button type="button" className="small" onClick={() => setRoles(proposal())}>
                {ca.missionUi.ours.reset}
              </button>
            </span>
          </div>
          <div className="two">
            <label>
              {ca.missionUi.ours.squadSize}
              <input
                name="squadSize"
                type="number"
                min={2}
                max={12}
                value={squadSize}
                onChange={(e) =>
                  setSquadSize(Math.max(2, Math.min(12, Number(e.target.value) || 8)))
                }
              />
            </label>
            {groupPresets.length > 0 && (
              <label>
                {ca.missionUi.ours.fromPreset}
                <select name="groupPreset" value="" onChange={(e) => fromPreset(e.target.value)}>
                  <option value="">{ca.missionUi.ours.fromPresetNone}</option>
                  {groupPresets.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <p className="muted small">{ca.missionUi.ours.rolesHint}</p>
          {aircrew && (
            <p className="muted small aircrew">
              {ca.missionUi.ours.aircrew(
                loadouts.find((l) => String(l.id) === aircrew.loadoutId)?.name ??
                  assets?.units.find((u) => u.className === aircrew.unitClass)?.displayName ??
                  aircrew.unitClass ??
                  '',
              )}
            </p>
          )}
          <div
            className="role-table compact"
            role="group"
            aria-label={ca.missionUi.ours.rolesTitle}
          >
            <div className="role-row head" aria-hidden="true">
              <span />
              <span>{ca.missionUi.ours.roleFunction}</span>
              <span>{ca.missionUi.ours.roleLabel}</span>
              <span>{ca.missionUi.ours.roleUnit}</span>
              <span>{ca.missionUi.ours.roleLoadout}</span>
              <span>{ca.missionUi.ours.roleCount}</span>
              <span />
            </div>
            {roles.map((r, i) => (
              <div key={`${r.role}-${i}`} className="role-row">
                {/* What the class actually looks like, out of the game's own files. Always
                    a cell, drawn or not: a row with no class drew nothing here, and every
                    control slid one column left, under the next column's heading. */}
                <span className="role-picture">
                  <AssetPicture
                    size={24}
                    assetClass={r.unitClass}
                    modSetHash={draft.modSetHash}
                    picture={assets?.units.find((u) => u.className === r.unitClass)?.picture}
                    name={r.unitClass ?? undefined}
                  />
                </span>
                <select
                  aria-label={`${ca.missionUi.ours.roleFunction} ${i + 1}`}
                  value={r.role}
                  onChange={(e) => setRole(i, { role: e.target.value })}
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {roleName(role)}
                    </option>
                  ))}
                </select>
                <input
                  aria-label={`${ca.missionUi.ours.roleLabel} ${i + 1}`}
                  type="text"
                  placeholder={roleName(r.role)}
                  value={r.label ?? ''}
                  onChange={(e) => setRole(i, { label: e.target.value })}
                />
                <select
                  aria-label={`${ca.missionUi.ours.roleUnit} ${i + 1}`}
                  value={r.unitClass ?? ''}
                  onChange={(e) => setRole(i, { unitClass: e.target.value || null })}
                >
                  <option value="">{ca.missionUi.ours.roleUnitDefault}</option>
                  {(assets?.units ?? []).map((u) => (
                    <option key={u.className} value={u.className}>
                      {u.displayName}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={`${ca.missionUi.ours.roleLoadout} ${i + 1}`}
                  value={r.loadoutId ?? ''}
                  onChange={(e) => setRole(i, { loadoutId: e.target.value || null })}
                >
                  <option value="">{ca.missionUi.ours.roleLoadoutNone}</option>
                  {loadouts.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <input
                  aria-label={`${ca.missionUi.ours.roleCount} ${i + 1}`}
                  type="number"
                  min={1}
                  max={12}
                  value={r.count ?? 1}
                  onChange={(e) =>
                    setRole(i, { count: Math.max(1, Math.min(12, Number(e.target.value) || 1)) })
                  }
                />
                <span className="row">
                  <button
                    type="button"
                    className="icon"
                    aria-label={`${ca.missionUi.ours.roleUp} ${i + 1}`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <i className="bi bi-arrow-up" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="icon"
                    aria-label={`${ca.missionUi.ours.roleDown} ${i + 1}`}
                    disabled={i === roles.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <i className="bi bi-arrow-down" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="icon"
                    aria-label={`${ca.missionUi.ours.roleRemove} ${i + 1}`}
                    onClick={() => setRoles((c) => c.filter((_, j) => j !== i))}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </section>
      </QuickStart>
    </Shell>
  );
}
